import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCamera, faImages, faTag, faThLarge, faEye,
    faTimes, faRandom, faCalendarAlt, faHeart
} from '@fortawesome/free-solid-svg-icons';
import baseURL from '../url';
import './GaleriaHome.css';

const LOCAL_STORAGE_LIKES_KEY = 'galeria_me_encanta_likes';

export default function GaleriaHome() {
    const [todasLasFotos, setTodasLasFotos] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [categoriaActiva, setCategoriaActiva] = useState(null); // null = Todas
    const [loading, setLoading] = useState(true);
    const [fotoAmpliada, setFotoAmpliada] = useState(null);
    const [likesLocales, setLikesLocales] = useState({});
    const [animatingId, setAnimatingId] = useState(null);
    const [floatingHearts, setFloatingHearts] = useState([]);

    useEffect(() => {
        // Cargar likes guardados localmente
        try {
            const saved = localStorage.getItem(LOCAL_STORAGE_LIKES_KEY);
            if (saved) {
                setLikesLocales(JSON.parse(saved));
            }
        } catch (e) {
            console.error('Error al leer likes de localStorage', e);
        }

        cargarDatosGaleria();
    }, []);

    const cargarDatosGaleria = async () => {
        setLoading(true);
        try {
            const [resFotos, resCat] = await Promise.all([
                fetch(`${baseURL}/galeriaFotosGet.php?_t=${Date.now()}`),
                fetch(`${baseURL}/galeriaCategoriasGet.php?_t=${Date.now()}`)
            ]);
            const dataFotos = await resFotos.json();
            const dataCat = await resCat.json();

            const lista = (dataFotos.fotos || []).map(foto => ({
                ...foto,
                meEncanta: Number(foto.meEncanta) || 0
            }));
            // Ordenar fotos según prioridad de orden (orden ASC, luego idFoto DESC)
            const fotosOrdenadas = [...lista].sort((a, b) => {
                const diffOrden = (Number(a.orden) || 1) - (Number(b.orden) || 1);
                if (diffOrden !== 0) return diffOrden;
                return (Number(b.idFoto) || 0) - (Number(a.idFoto) || 0);
            });

            setTodasLasFotos(fotosOrdenadas);

            const categoriasOrdenadas = (dataCat.categorias || []).sort(
                (a, b) => (Number(a.orden) || 1) - (Number(b.orden) || 1)
            );
            setCategorias(categoriasOrdenadas);
        } catch (error) {
            console.error('Error al cargar la galería para el home:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSeleccionarCategoria = (idCat) => {
        setCategoriaActiva(idCat);
    };

    const handleReordenarAleatorio = () => {
        setTodasLasFotos(prev => [...prev].sort(() => 0.5 - Math.random()));
    };

    // Manejar Me Encanta ❤️
    const handleToggleMeEncanta = async (e, idFoto) => {
        if (e && e.stopPropagation) {
            e.stopPropagation();
        }

        const idNum = Number(idFoto);
        const yaTieneLike = Boolean(likesLocales[idNum]);
        const nuevoEstado = !yaTieneLike;

        // 1. Feedback visual inmediato (animación de pop)
        setAnimatingId(idNum);
        setTimeout(() => setAnimatingId(null), 600);

        // Si es un nuevo like, generamos micro-corazones flotantes
        if (nuevoEstado) {
            const newHeart = { id: Date.now() + Math.random(), idFoto: idNum };
            setFloatingHearts(prev => [...prev, newHeart]);
            setTimeout(() => {
                setFloatingHearts(prev => prev.filter(h => h.id !== newHeart.id));
            }, 1000);
        }

        // 2. Actualizar estado local de usuario y localStorage
        const updatedLikes = { ...likesLocales, [idNum]: nuevoEstado };
        setLikesLocales(updatedLikes);
        try {
            localStorage.setItem(LOCAL_STORAGE_LIKES_KEY, JSON.stringify(updatedLikes));
        } catch (err) {
            console.error('Error guardando like en storage', err);
        }

        // 3. Optimistic update en la lista de fotos
        setTodasLasFotos(prev =>
            prev.map(f => {
                if (Number(f.idFoto) === idNum) {
                    const actualCount = Number(f.meEncanta) || 0;
                    const delta = nuevoEstado ? 1 : -1;
                    return { ...f, meEncanta: Math.max(0, actualCount + delta) };
                }
                return f;
            })
        );

        // Si la foto está en el lightbox, actualizarla también
        if (fotoAmpliada && Number(fotoAmpliada.idFoto) === idNum) {
            setFotoAmpliada(prev => {
                if (!prev) return null;
                const actualCount = Number(prev.meEncanta) || 0;
                const delta = nuevoEstado ? 1 : -1;
                return { ...prev, meEncanta: Math.max(0, actualCount + delta) };
            });
        }

        // 4. Petición al backend
        try {
            const response = await fetch(`${baseURL}/galeriaFotoMeEncanta.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    idFoto: idNum,
                    accion: nuevoEstado ? 'like' : 'unlike'
                })
            });
            const data = await response.json();
            if (data && data.success && typeof data.meEncanta !== 'undefined') {
                const totalServidor = Number(data.meEncanta);
                setTodasLasFotos(prev =>
                    prev.map(f => (Number(f.idFoto) === idNum ? { ...f, meEncanta: totalServidor } : f))
                );
                if (fotoAmpliada && Number(fotoAmpliada.idFoto) === idNum) {
                    setFotoAmpliada(prev => (prev ? { ...prev, meEncanta: totalServidor } : null));
                }
            }
        } catch (err) {
            console.error('Error al actualizar me encanta en el servidor:', err);
        }
    };

    // Filtrar fotos por la categoría activa (si está seleccionada)
    const fotosFiltradas = categoriaActiva === null
        ? todasLasFotos
        : todasLasFotos.filter(f => String(f.idCategoriaGaleria) === String(categoriaActiva));

    // Máximo 12 fotos para completar 4 columnas y 3 filas
    const fotosAMostrar = fotosFiltradas.slice(0, 12);

    // Si aún no hay fotos cargadas en la base de datos, no mostramos una sección vacía rota
    if (!loading && todasLasFotos.length === 0) {
        return null;
    }

    return (
        <section className="galeriaHomeSection" id="galeria-fotos">
            <div className="galeriaHomeBgGlow"></div>
            <div className="galeriaHomeContainer">
                {/* Controles de Categoría y Mezclar */}
                <div className="galeriaHomeHeader">
                    <div className="galeriaHomeControls">
                        {categorias.length > 0 && (
                            <div className="galeriaHomePills">
                                <button
                                    type="button"
                                    className={`galeriaPill ${categoriaActiva === null ? 'active' : ''}`}
                                    onClick={() => handleSeleccionarCategoria(null)}
                                >
                                    <FontAwesomeIcon icon={faThLarge} /> Todas
                                </button>
                                {categorias.map(cat => (
                                    <button
                                        key={cat.idCategoriaGaleria}
                                        type="button"
                                        className={`galeriaPill ${categoriaActiva === cat.idCategoriaGaleria ? 'active' : ''}`}
                                        onClick={() => handleSeleccionarCategoria(cat.idCategoriaGaleria)}
                                    >
                                        <FontAwesomeIcon icon={faTag} /> {cat.nombre}
                                    </button>
                                ))}
                            </div>
                        )}

                        <button
                            type="button"
                            className="btnMezclar"
                            onClick={handleReordenarAleatorio}
                            title="Mezclar fotos aleatoriamente"
                            aria-label="Mezclar fotos aleatoriamente"
                        >
                            <FontAwesomeIcon icon={faRandom} />
                            <span>Aleatorio</span>
                        </button>
                    </div>
                </div>

                {/* Cuadrícula de fotos */}
                {loading ? (
                    <div className="galeriaHomeLoading">
                        <div className="galeriaHomeSpinner"></div>
                        <p>Cargando fotos de la galería...</p>
                    </div>
                ) : fotosAMostrar.length === 0 ? (
                    <div className="galeriaHomeEmpty">
                        <FontAwesomeIcon icon={faImages} />
                        <p>No hay fotos disponibles en esta categoría.</p>
                    </div>
                ) : (
                    <div className="galeriaHomeGrid">
                        {fotosAMostrar.map(foto => {
                            const isLiked = Boolean(likesLocales[foto.idFoto]);
                            const isPopping = animatingId === Number(foto.idFoto);

                            return (
                                <div
                                    key={foto.idFoto}
                                    className="galeriaHomeCard"
                                    onClick={() => setFotoAmpliada(foto)}
                                    title="Clic para ampliar foto"
                                >
                                    <div className="galeriaHomeImgWrapper">
                                        <img
                                            src={foto.imagen}
                                            alt={foto.nombre || 'Foto de galería'}
                                            loading="lazy"
                                            decoding="async"
                                            width="350"
                                            height="350"
                                        />

                                        {/* Botón flotante 'Me encanta' */}
                                        <button
                                            type="button"
                                            className={`btnMeEncantaCard ${isLiked ? 'liked' : ''} ${isPopping ? 'pop' : ''}`}
                                            onClick={(e) => handleToggleMeEncanta(e, foto.idFoto)}
                                            title={isLiked ? "Ya no me encanta" : "Me encanta"}
                                            aria-label="Me encanta"
                                        >
                                            <FontAwesomeIcon icon={faHeart} className="meEncantaHeart" />
                                            <span className="meEncantaCount">{foto.meEncanta || 0}</span>

                                            {/* Micro corazones flotantes animados */}
                                            {floatingHearts
                                                .filter(h => h.idFoto === Number(foto.idFoto))
                                                .map(h => (
                                                    <span key={h.id} className="floatingHeart">
                                                        <FontAwesomeIcon icon={faHeart} />
                                                    </span>
                                                ))}
                                        </button>

                                        {/* Overlay para ampliar */}
                                        <div className="galeriaHomeOverlay">
                                            <span className="overlayIcon">
                                                <FontAwesomeIcon icon={faEye} />
                                            </span>
                                            <span className="overlayPrompt">Ampliar</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Modal Lightbox de Foto Ampliada */}
            {fotoAmpliada && (
                <div className="galeriaLightboxOverlay" onClick={() => setFotoAmpliada(null)}>
                    <div className="galeriaLightboxContent" onClick={e => e.stopPropagation()}>
                        <button
                            type="button"
                            className="galeriaLightboxClose"
                            onClick={() => setFotoAmpliada(null)}
                            aria-label="Cerrar"
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                        <div className="galeriaLightboxImgBox">
                            <img src={fotoAmpliada.imagen} alt={fotoAmpliada.nombre} />
                        </div>
                        <div className="galeriaLightboxInfo">
                            <div className="lightboxHeaderRow">
                                <h3>{fotoAmpliada.nombre}</h3>
                                <div className="lightboxMetaActions">
                                    <span className="lightboxBadge">
                                        <FontAwesomeIcon icon={faTag} /> {fotoAmpliada.categoria_nombre}
                                    </span>

                                    {/* Botón Me encanta dentro del Lightbox */}
                                    <button
                                        type="button"
                                        className={`btnMeEncantaLightbox ${likesLocales[fotoAmpliada.idFoto] ? 'liked' : ''} ${animatingId === Number(fotoAmpliada.idFoto) ? 'pop' : ''}`}
                                        onClick={(e) => handleToggleMeEncanta(e, fotoAmpliada.idFoto)}
                                        title={likesLocales[fotoAmpliada.idFoto] ? "Ya no me encanta" : "Me encanta"}
                                    >
                                        <FontAwesomeIcon icon={faHeart} className="lightboxHeart" />
                                        <span className="lightboxLikeText">
                                            {likesLocales[fotoAmpliada.idFoto] ? 'Te encanta' : 'Me encanta'}
                                        </span>
                                        <span className="lightboxLikeCount">
                                            {fotoAmpliada.meEncanta || 0}
                                        </span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}
